import React, { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, Save } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const LANGS = [["fr", "Français"], ["nl", "Nederlands"], ["en", "English"]];

export default function SurveyManager() {
  const { authHeaders } = useAuth();
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("fr");
  const [msg, setMsg] = useState("");

  const load = async () => {
    const { data } = await api.get("/admin/survey", { headers: authHeaders() });
    setSurvey(data);
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const setQ = (lang, val) => setSurvey((s) => ({ ...s, question: { ...s.question, [lang]: val } }));
  const setOpt = (idx, lang, val) =>
    setSurvey((s) => {
      const options = [...s.options];
      options[idx] = { ...options[idx], label: { ...options[idx].label, [lang]: val } };
      return { ...s, options };
    });
  const addOpt = () => setSurvey((s) => ({ ...s, options: [...s.options, { id: crypto.randomUUID(), label: { fr: "", nl: "", en: "" } }] }));
  const delOpt = (idx) => setSurvey((s) => ({ ...s, options: s.options.filter((_, i) => i !== idx) }));

  const save = async () => {
    setSaving(true);
    setMsg("");
    try {
      await api.put("/admin/survey", {
        active: survey.active, cooldown_hours: Number(survey.cooldown_hours) || 24,
        question: survey.question, options: survey.options,
      }, { headers: authHeaders() });
      setMsg("Sondage enregistré ✓");
    } catch {
      setMsg("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(""), 2500);
    }
  };

  if (loading) return <div className="flex h-40 items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-caneat-orange" /></div>;
  if (!survey) return null;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="caneat-title text-2xl text-caneat-ink">Sondage</h2>
        <label className="flex cursor-pointer items-center gap-2" data-testid="survey-active-toggle">
          <input type="checkbox" checked={survey.active} onChange={(e) => setSurvey({ ...survey, active: e.target.checked })} className="h-5 w-5 accent-caneat-orange" />
          <span className="text-sm font-bold text-caneat-ink">Sondage actif</span>
        </label>
      </div>

      <div className="rounded-2xl bg-white p-4 ring-1 ring-caneat-cream">
        <label className="mb-1 block text-xs font-bold uppercase text-caneat-muted">Réafficher après (heures)</label>
        <input type="number" min={0} value={survey.cooldown_hours}
          onChange={(e) => setSurvey({ ...survey, cooldown_hours: e.target.value })}
          data-testid="survey-cooldown"
          className="w-32 rounded-xl border-2 border-caneat-cream px-3 py-2 text-sm font-semibold outline-none focus:border-caneat-orange" />
      </div>

      <div className="flex gap-1 rounded-full bg-caneat-pageCream p-1">
        {LANGS.map(([code, label]) => (
          <button key={code} onClick={() => setTab(code)}
            className={`flex-1 rounded-full py-2 text-xs font-bold ${tab === code ? "bg-caneat-orange text-white" : "text-caneat-muted"}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="rounded-2xl bg-white p-4 ring-1 ring-caneat-cream">
        <label className="mb-1 block text-xs font-bold uppercase text-caneat-muted">Question ({tab})</label>
        <input value={survey.question[tab] || ""} onChange={(e) => setQ(tab, e.target.value)}
          data-testid={`survey-question-${tab}`}
          className="w-full rounded-xl border-2 border-caneat-cream px-3 py-2 text-sm font-semibold outline-none focus:border-caneat-orange" />
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs font-bold uppercase text-caneat-muted">Réponses</span>
          <button onClick={addOpt} data-testid="add-option-btn" className="flex items-center gap-1 rounded-full bg-caneat-orange/10 px-3 py-1.5 text-xs font-bold text-caneat-orange">
            <Plus className="h-3.5 w-3.5" /> Ajouter
          </button>
        </div>
        {survey.options.map((opt, idx) => (
          <div key={opt.id} className="flex items-center gap-2" data-testid={`survey-option-row-${idx}`}>
            <input value={opt.label[tab] || ""} onChange={(e) => setOpt(idx, tab, e.target.value)}
              placeholder={`Réponse ${idx + 1} (${tab})`}
              className="flex-1 rounded-xl border-2 border-caneat-cream px-3 py-2 text-sm outline-none focus:border-caneat-orange" />
            <button onClick={() => delOpt(idx)} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-red-50 text-red-500"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>

      {msg && <p className="text-sm font-bold text-caneat-orange">{msg}</p>}

      <button onClick={save} disabled={saving} data-testid="survey-save"
        className="flex min-h-[50px] w-full items-center justify-center gap-2 rounded-full bg-caneat-orange text-base font-extrabold uppercase text-white disabled:opacity-60">
        {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Save className="h-5 w-5" /> Enregistrer</>}
      </button>
    </div>
  );
}
